/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useRef, useEffect } from "react";

interface LightRaysProps {
  raysOrigin?: "top-center" | "top-left" | "top-right" | "center";
  raysColor?: string;
  raysSpeed?: number;
  lightSpread?: number;
  rayLength?: number;
  pulsating?: boolean;
  fadeDistance?: number;
  saturation?: number;
  followMouse?: boolean;
  mouseInfluence?: number;
  noiseAmount?: number;
  distortion?: number;
  className?: string;
  style?: React.CSSProperties;
}

const LightRays: React.FC<LightRaysProps> = ({
  raysOrigin = "top-center",
  raysColor = "#ffffff",
  raysSpeed = 1,
  className,
  style,
}) => {
  // Use 'any' to bypass strict DOM type errors on the Ref
  const canvasRef = useRef<any>(null);

  useEffect(() => {
    // 1. Bypass TypeScript 'window' check by casting globalThis
    const safeWindow = globalThis as any;

    // 2. Ensure we are in the browser
    if (!safeWindow.window) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let tick = 0;

    const resize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };

    const draw = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Setup Origin
      let x = canvas.width / 2;
      let y = 0;
      if (raysOrigin === "top-left") x = 0;
      else if (raysOrigin === "top-right") x = canvas.width;
      else if (raysOrigin === "center") {
        x = canvas.width / 2;
        y = canvas.height / 2;
      }

      // Draw Rays
      const rayCount = 20;
      const baseAngle = tick * 0.005 * raysSpeed;

      ctx.save();
      ctx.translate(x, y);

      // Create rays
      for (let i = 0; i < rayCount; i++) {
        const angle = baseAngle + (i / rayCount) * Math.PI * 2;
        const length = Math.max(canvas.width, canvas.height) * 1.5;

        // Dynamic transparency for "pulsing" feel
        const alpha = ((Math.sin(angle * 5 + tick * 0.02) + 1) / 2) * 0.3;

        const gradient = ctx.createLinearGradient(
          0,
          0,
          Math.cos(angle) * length,
          Math.sin(angle) * length,
        );
        gradient.addColorStop(0, raysColor);
        gradient.addColorStop(1, "transparent");

        ctx.fillStyle = gradient;
        ctx.globalAlpha = alpha * 0.3; // Low opacity for subtle effect

        // Draw trapezoid ray
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(
          Math.cos(angle - 0.1) * length,
          Math.sin(angle - 0.1) * length,
        );
        ctx.lineTo(
          Math.cos(angle + 0.1) * length,
          Math.sin(angle + 0.1) * length,
        );
        ctx.closePath();
        ctx.fill();
      }

      ctx.restore();

      tick++;
      // Use safeWindow instead of window
      animationFrameId = safeWindow.requestAnimationFrame(draw);
    };

    safeWindow.addEventListener("resize", resize);
    resize();
    draw();

    return () => {
      safeWindow.removeEventListener("resize", resize);
      safeWindow.cancelAnimationFrame(animationFrameId);
    };
  }, [raysOrigin, raysColor, raysSpeed]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ display: "block", width: "100%", height: "100%", ...style }}
    />
  );
};

export default LightRays;
